import { useEffect, useState } from "react";
import axios from "axios";
import { errorText } from "../../../shared/toastHelper";

export const SEND_TICKET_COUNTS_NOW_LABEL = "Send ticket counts now";
export const SEND_TICKET_COUNTS_NOW_TITLE = "Send current ticket counts?";

export const ticketCountsEventLabel = (title, humanStartDate) => [title, humanStartDate].filter(Boolean).join(" — ");

export const sendTicketCountsNowMessage = (contact, eventLabel) => {
  const recipient = contact?.name && contact.name !== contact.email
    ? `${contact.name} (${contact.email})`
    : contact?.email;

  return `${recipient} will get the ticket count email for ${eventLabel} now. Their regular schedule doesn't change.`;
};

export const sendTicketCountsNow = (teamId, calendarEventContactId, csrfToken) => axios.post(
  `/teams/${teamId}/calendar_event_contacts/${calendarEventContactId}/ticket_count_send`,
  {},
  { headers: { "X-CSRF-TOKEN": csrfToken } },
);

export const sendTicketCountsNowSuccessMessage = (calendarEventContact) => (
  `Ticket counts sent to ${calendarEventContact.contact.email}`
);

export const sendTicketCountsNowErrorMessage = (error) => (
  errorText(error, "Couldn't send ticket counts. Please try again.")
);

export const refusedCalendarEventContact = (error) => error?.response?.data?.calendar_event_contact || null;

const cooldownSeconds = (calendarEventContact) => calendarEventContact?.ticket_counts_send_now_status?.available_in_seconds;
const cooldownKey = (calendarEventContact) => `${calendarEventContact.id}:${calendarEventContact.ticket_counts_send_now_status?.sent_at}`;

export const useSendTicketCountsNow = (calendarEventContacts) => {
  const [deadlines, setDeadlines] = useState(() => new Map());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const unseen = (calendarEventContacts || [])
      .filter((calendarEventContact) => cooldownSeconds(calendarEventContact) && !deadlines.has(cooldownKey(calendarEventContact)));
    if (unseen.length === 0) return;

    const current = Date.now();
    setDeadlines((previous) => {
      const next = new Map(previous);
      unseen.forEach((calendarEventContact) => {
        const key = cooldownKey(calendarEventContact);
        if (!next.has(key)) next.set(key, current + cooldownSeconds(calendarEventContact) * 1000);
      });
      return next;
    });
  }, [calendarEventContacts, deadlines]);

  useEffect(() => {
    const current = Date.now();
    const upcoming = [...deadlines.values()].filter((endsAt) => endsAt > current);
    if (upcoming.length === 0) return undefined;

    const timer = setTimeout(() => setNow(Date.now()), Math.min(...upcoming) - current + 250);
    return () => clearTimeout(timer);
  }, [deadlines, now]);

  return (calendarEventContact) => {
    const status = calendarEventContact.ticket_counts_send_now_status;
    if (status?.available) return { available: true, reason: null };

    const endsAt = deadlines.get(cooldownKey(calendarEventContact));
    if (endsAt && now >= endsAt) return { available: true, reason: null };
    return { available: false, reason: status?.reason || null };
  };
};
